import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Phone, Mail, MapPin, ShieldAlert, ShieldOff, LifeBuoy, MessageSquare, ExternalLink, Loader2, PhoneOff } from "lucide-react";

const CRIMSON = "#bf3b4f";

// Plain text from a field that may hold rich-text HTML; keeps line breaks.
const strip = (v) => String(v || "").replace(/<br\s*\/?>|<\/p>|<\/li>/gi, "\n").replace(/<[^>]*>/g, "")
  .replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/[ \t]+/g, " ").replace(/\n\s*\n+/g, "\n").trim();
const telHref = (n) => `tel:${String(n).replace(/[^\d+]/g, "")}`;
const contactName = (c) => c.name1 || c.contact || "Unnamed contact";

// Click a participant / staff name on the roster to see how to reach them.
// A non-modal popover: Escape or a click outside closes it, and it never opens
// the shift underneath (events are stopped here because React bubbles them out
// of the portal to the shift card button).
export function ContactCardTrigger({ kind, id, name, children, className = "", testid, disabled = false }) {
  const [open, setOpen] = useState(false);
  if (!id || disabled) return children ?? name;
  const stop = (e) => e.stopPropagation();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span role="button" tabIndex={0} title={`Contact details for ${name}`}
          data-testid={testid || `contact-trigger-${kind}-${id}`}
          onClick={stop}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }
          }}
          className={`cursor-pointer hover:underline decoration-dotted underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bf3b4f]/40 rounded-sm ${className}`}>
          {children ?? name}
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" side="right" collisionPadding={12}
        className="w-80 p-0 overflow-hidden" data-testid={`contact-card-${kind}-${id}`}
        onClick={stop} onKeyDown={(e) => { if (e.key !== "Escape") stop(e); }}>
        {open && <CardBody kind={kind} id={id} name={name} />}
      </PopoverContent>
    </Popover>
  );
}

function CardBody({ kind, id, name }) {
  const q = useQuery({
    queryKey: ["contact-card", kind, id],
    queryFn: () => (kind === "participant" ? api.participant(id) : api.staffProfile(id)),
    retry: false,
  });
  const profileHref = kind === "participant"
    ? `/participants?open=${encodeURIComponent(id)}` : `/staff-profiles?open=${encodeURIComponent(id)}`;

  return (
    <div className="text-xs text-slate-700">
      <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-slate-100 bg-slate-50">
        {q.data?.photo
          ? <img src={q.data.photo} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
          : <div className="h-8 w-8 rounded-full text-white flex items-center justify-center text-[11px] font-semibold shrink-0"
              style={{ backgroundColor: kind === "participant" ? "#a32f42" : "#334155" }}>
              {String(name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
            </div>}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 truncate">{q.data?.display_name || name}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            {kind === "participant" ? "Participant" : (q.data?.primary_role || "Staff")}
          </div>
        </div>
        <a href={profileHref} target="_blank" rel="noreferrer" title="Open full profile in a new tab"
          className="shrink-0 text-slate-400 hover:text-[#bf3b4f]" data-testid="contact-card-profile-link">
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      <div className="max-h-[60vh] overflow-y-auto px-3 py-2.5 space-y-3">
        {q.isLoading ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : q.isError ? (
          <div className="py-3 text-slate-500" data-testid="contact-card-error">
            {q.error?.response?.status === 404 ? "This record isn't available (it may be inactive)." : "Couldn't load contact details."}
          </div>
        ) : kind === "participant" ? <ParticipantBody d={q.data} /> : <StaffBody d={q.data} />}
      </div>
    </div>
  );
}

function ParticipantBody({ d }) {
  const contacts = d.participant_contacts || [];
  const guardians = contacts.filter((c) => c.legal_guardian);
  const emergency = contacts.filter((c) => c.emergency_contact && !c.legal_guardian);
  const guardianOnly = d.contact_via_guardian_only === "Yes";
  const phones = [["Mobile", d.mobile_number], ["Phone", d.phone_number]].filter(([, v]) => v);
  const comms = strip(d.communication_requirements);
  const noAnswer = strip(d.procedures_for_unsuccessful_contact_at_shift_start);
  const extraPref = strip(d.additional_contact_preference);

  return (
    <>
      {guardianOnly && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2" data-testid="contact-card-guardian-only">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-800">
            <ShieldAlert className="h-3.5 w-3.5" /> Contact via guardian only
          </div>
          {guardians.length ? (
            <div className="mt-1.5 space-y-1.5">{guardians.map((c, i) => <PersonRow key={c.name || i} c={c} />)}</div>
          ) : (
            <p className="mt-1 text-[11px] text-amber-800">No legal guardian is recorded. Add one on the participant's Addresses &amp; Contacts.</p>
          )}
        </div>
      )}

      <Section title={guardianOnly ? "Participant's own details" : "Primary contact"}>
        {guardianOnly && phones.length > 0 && (
          <div className="flex items-center gap-1 text-[10px] text-amber-700 font-medium mb-1"><PhoneOff className="h-3 w-3" />Don't use for routine contact</div>
        )}
        {phones.length === 0 && !d.email && <Muted>No phone or email recorded.</Muted>}
        {phones.map(([l, v], i) => <PhoneLine key={l} label={l} value={v} primary={i === 0 && !guardianOnly} />)}
        {d.email && <EmailLine value={d.email} />}
        {(d.contact_preference || extraPref) && (
          <div className="text-[11px] text-slate-500 mt-1">
            {d.contact_preference && <span>Prefers <b className="text-slate-700">{d.contact_preference}</b>. </span>}
            {extraPref}
          </div>
        )}
      </Section>

      {!guardianOnly && guardians.length > 0 && (
        <Section title={guardians.length > 1 ? "Legal guardians" : "Legal guardian"}>
          <div className="space-y-1.5">{guardians.map((c, i) => <PersonRow key={c.name || i} c={c} />)}</div>
        </Section>
      )}

      {emergency.length > 0 && (
        <Section title="Emergency contacts" icon={LifeBuoy}>
          <div className="space-y-1.5">{emergency.map((c, i) => <PersonRow key={c.name || i} c={c} />)}</div>
        </Section>
      )}

      {(comms || d.language) && (
        <Section title="Communication" icon={MessageSquare}>
          {d.language && <div className="text-[11px]"><span className="text-slate-400">Language:</span> {d.language}</div>}
          {comms && <Clamp text={comms} />}
        </Section>
      )}

      {d.primary_address && (
        <Section title="Address" icon={MapPin}><div className="text-[11px] leading-snug">{d.primary_address}</div></Section>
      )}

      {noAnswer && (
        <Section title="If no answer at shift start"><Clamp text={noAnswer} /></Section>
      )}
    </>
  );
}

function StaffBody({ d }) {
  return (
    <>
      <Section title="Primary contact">
        {!d.cell_number && !d.company_email && <Muted>No phone or email recorded.</Muted>}
        {d.cell_number && <PhoneLine label="Mobile" value={d.cell_number} primary />}
        {d.company_email && <EmailLine value={d.company_email} />}
      </Section>
      {(d.emer_name || d.emer_phone) && (
        <Section title="Emergency contact" icon={LifeBuoy}>
          <PersonRow c={{ name1: d.emer_name, relationship: d.emer_relation, phone: d.emer_phone }} />
        </Section>
      )}
      {d.languages && (
        <Section title="Languages" icon={MessageSquare}><div className="text-[11px]">{strip(d.languages)}</div></Section>
      )}
    </>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
        {Icon && <Icon className="h-3 w-3" />}{title}
      </div>
      {children}
    </div>
  );
}

function PhoneLine({ label, value, primary }) {
  return (
    <a href={telHref(value)} className="flex items-center gap-1.5 py-0.5 group" data-testid="contact-card-phone">
      <Phone className="h-3.5 w-3.5 shrink-0" style={{ color: primary ? CRIMSON : "#94a3b8" }} />
      <span className={`tabular-nums group-hover:underline ${primary ? "text-sm font-semibold text-slate-900" : "text-slate-700"}`}>{value}</span>
      <span className="text-[10px] text-slate-400">{label}</span>
    </a>
  );
}

function EmailLine({ value }) {
  return (
    <a href={`mailto:${value}`} className="flex items-center gap-1.5 py-0.5 group min-w-0">
      <Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <span className="truncate group-hover:underline">{value}</span>
    </a>
  );
}

function PersonRow({ c }) {
  return (
    <div className="rounded border border-slate-200 bg-white px-2 py-1.5" data-testid="contact-card-person">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-semibold text-slate-800">{contactName(c)}</span>
        {c.relationship && <span className="text-[10px] text-slate-400">{c.relationship}</span>}
        {c.consent_withdrawn ? (
          <span className="inline-flex items-center gap-0.5 rounded bg-slate-100 border border-slate-300 px-1 text-[9px] font-semibold text-slate-600"
            title={c.consent_withdrawn_reason || "Consent to share information has been withdrawn"}>
            <ShieldOff className="h-2.5 w-2.5" />Consent withdrawn
          </span>
        ) : null}
      </div>
      {c.phone ? <PhoneLine label="" value={c.phone} /> : <Muted>No phone recorded.</Muted>}
      {c.email && <EmailLine value={c.email} />}
    </div>
  );
}

function Clamp({ text }) {
  const [more, setMore] = useState(false);
  const long = text.length > 180;
  return (
    <div className="text-[11px] leading-snug whitespace-pre-line">
      {long && !more ? `${text.slice(0, 180).trimEnd()}… ` : text}
      {long && (
        <button type="button" onClick={() => setMore((m) => !m)} className="text-[#bf3b4f] font-medium hover:underline ml-0.5">
          {more ? "Less" : "More"}
        </button>
      )}
    </div>
  );
}

const Muted = ({ children }) => <div className="text-[11px] text-slate-400">{children}</div>;
